import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import UploadFlowPanel from "../Components/UploadFlow/UploadFlowPanel";
import SketchUpload from "./sketch-upload";
import { useGlobalAuthContext } from "../context/AuthContext";
import MainHomeHeaderActions from "../Components/UserHome/MainHomeHeaderActions";
import MobileFloorPlanSlider from "../Components/UserHome/MobileFloorPlanSlider";
import { ReactComponent as MfLogo } from "../Assets/mf-logo.svg";
import {
  BENTO_SURFACE_BORDER_RADIUS,
  CARD_MAX_WIDTH,
} from "../Components/UploadStates/uploadCardLayout";
import {
  apiRoomNate,
  useDeleteSavedLayoutMutation,
  useGetSavedLayoutSpecficUserQuery,
} from "../Slices/roomNateApis";
import { baseUrl } from "../utils/baseUrl";
import {
  APP_VERSION,
  legalLinkSx,
  legalVersionLabelSx,
  MF_PRIVACY_URL,
  MF_SUPPORT_URL,
  MF_TERMS_URL,
} from "../utils/legalLinks";
import DeleteDialog from "../Components/DeleteDilogue";

/** Same flattening as `filterLayoutsFromNode` in `sketch-upload.js` (one tree node). */
function filterLayoutsNode(data) {
  let result = [];
  if (data.image) {
    result.push(data);
  }
  if (data.children) {
    data.children.forEach((child) => {
      result = result.concat(filterLayoutsNode(child));
    });
  }
  return result;
}

/** All folder nodes in the tree, flattened and sorted A–Z by name. */
function collectFoldersFlatSorted(nodes) {
  const out = [];
  function walk(n) {
    if (!n.image) {
      out.push(n);
    }
    if (n.children) {
      n.children.forEach(walk);
    }
  }
  (nodes || []).forEach(walk);
  return out.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
}

function countPlansUnderFolderNode(node) {
  return filterLayoutsNode(node).filter(
    (i) => i.image && i?.userSavedLayoutsCount > 0
  ).length;
}

function findLayoutByUuid(nodes, uuid) {
  if (!uuid || !nodes?.length) return null;
  for (const node of nodes) {
    if (node.uuid === uuid) return node;
    if (node.children?.length) {
      const found = findLayoutByUuid(node.children, uuid);
      if (found) return found;
    }
  }
  return null;
}

function findLayoutByFolderId(nodes, folderId) {
  if (!folderId || !nodes?.length) return null;
  for (const node of nodes) {
    if (node._id === folderId) return node;
    if (node.children?.length) {
      const found = findLayoutByFolderId(node.children, folderId);
      if (found) return found;
    }
  }
  return null;
}

/** Single MUI spacing unit for gaps: folders↔upload, top row↔library, and header↔grid (md+). */
const BENTO_SPACING = 2;

const LONG_PRESS_MS = 500;

/**
 * `/main` behavior:
 * - Guest + guest upload: centered upload only (embed-mode).
 * - Guest + no guest upload: /signup (RequireAuth; defensive Navigate here).
 * - Signed in: header (logo + actions), then md+ two columns — left sidebar (upload, folder list)
 *   and right primary (floor plans grid); xs stacks folders → upload → library. Profile is in the header menu.
 */
const UserMainHome = () => {
  const { currentUserSessionStorage, allowGuestUpload } = useGlobalAuthContext();

  const isGuest = !currentUserSessionStorage;

  if (isGuest && !allowGuestUpload) {
    return <Navigate to="/signup" replace />;
  }

  if (isGuest && allowGuestUpload) {
    return <GuestGuestUploadMain />;
  }

  return <SignedInMain />;
};

/** Guest + allowGuestUpload: upload only, vertically centered like external-ai-upload. */
function GuestGuestUploadMain() {
  useEffect(() => {
    document.body.classList.add("embed-mode");
    return () => document.body.classList.remove("embed-mode");
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        minHeight: {
          xs: "calc(100dvh - 56px)",
          sm: "calc(100dvh - 48px)",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <UploadFlowPanel layoutMode="embedded" />
    </Box>
  );
}

function SignedInMain() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const libraryFillHeight = useMediaQuery(theme.breakpoints.up("md"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);

  /** Imperative handle exposed by `SketchUpload`; lets the mobile slider open the same options dialog. */
  const sketchUploadHandleRef = useRef(null);

  const selectedFolderIdRef = useRef(selectedFolderId);
  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId;
  }, [selectedFolderId]);

  const {
    data: layoutData = [],
    refetch: refetchLayouts,
    isLoading: savedLayoutsLoading,
    isFetching: savedLayoutsFetching,
  } = useGetSavedLayoutSpecficUserQuery(undefined, { skip: false });

  const [deleteLayoutMutation] = useDeleteSavedLayoutMutation();

  const [editingFolderId, setEditingFolderId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const renameOriginalRef = useRef("");

  const [mobilePopover, setMobilePopover] = useState(null);
  const longPressTimerRef = useRef(null);
  const suppressFolderRowClickRef = useRef(false);

  const [contextMenu, setContextMenu] = useState(null);

  const [folderPendingDelete, setFolderPendingDelete] = useState(null);

  const [draggingPlanId, setDraggingPlanId] = useState(null);
  const [dropTargetFolderId, setDropTargetFolderId] = useState(null);

  const authHeaders = useCallback(() => {
    return {
      Authorization: `Bearer ${currentUserSessionStorage}`,
    };
  }, [currentUserSessionStorage]);

  const saveFolderName = useCallback(
    async (folderId, name) => {
      const trimmed = (name || "").trim();
      if (!trimmed || !folderId) return;
      await axios.put(
        `${baseUrl}/savedLayout/edit/${folderId}`,
        { name: trimmed },
        { headers: authHeaders() }
      );
      dispatch(apiRoomNate.util.invalidateTags(["savedLayouts"]));
    },
    [authHeaders, dispatch]
  );

  const cancelRename = useCallback(() => {
    setEditingFolderId(null);
    setRenameDraft("");
    renameOriginalRef.current = "";
  }, []);

  const commitRename = useCallback(async () => {
    if (!editingFolderId) return;
    const trimmed = renameDraft.trim();
    const orig = renameOriginalRef.current;
    if (!trimmed || trimmed === orig) {
      cancelRename();
      return;
    }
    try {
      await saveFolderName(editingFolderId, trimmed);
    } finally {
      cancelRename();
    }
  }, [editingFolderId, renameDraft, saveFolderName, cancelRename]);

  const beginDesktopRename = useCallback((folder) => {
    if (!isMdUp) return;
    renameOriginalRef.current = folder.name || "";
    setRenameDraft(folder.name || "");
    setEditingFolderId(folder._id);
  }, [isMdUp]);

  const handleRenameKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitRename();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelRename();
      }
    },
    [commitRename, cancelRename]
  );

  const openMobileFolderPopover = useCallback((anchorEl, folder) => {
    renameOriginalRef.current = folder.name || "";
    setRenameDraft(folder.name || "");
    setMobilePopover({ anchorEl, folder });
  }, []);

  const closeMobileFolderPopover = useCallback(() => {
    setMobilePopover(null);
  }, []);

  const touchStartFolder = useCallback(
    (e, folder) => {
      if (isMdUp) return;
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      const target = e.currentTarget;
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        openMobileFolderPopover(target, folder);
      }, LONG_PRESS_MS);
    },
    [isMdUp, openMobileFolderPopover]
  );

  const touchClearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleFolderRowClick = useCallback(
    (folderId) => {
      if (suppressFolderRowClickRef.current) {
        suppressFolderRowClickRef.current = false;
        return;
      }
      setSelectedFolderId(folderId);
    },
    []
  );

  const handleFolderContextMenu = useCallback(
    (e, folder) => {
      if (!isMdUp) return;
      e.preventDefault();
      setContextMenu({ top: e.clientY, left: e.clientX, folder });
    },
    [isMdUp]
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const reparentDirectChildrenToRoot = useCallback(
    async (folder) => {
      const children = folder?.children || [];
      for (const child of children) {
        await axios.put(
          `${baseUrl}/savedLayout/edit/${child._id}`,
          { parent: null },
          { headers: authHeaders() }
        );
      }
    },
    [authHeaders]
  );

  const confirmDeleteFolder = useCallback(async () => {
    const folder = folderPendingDelete;
    if (!folder?._id) return;
    const deletedId = folder._id;
    try {
      await reparentDirectChildrenToRoot(folder);
      await deleteLayoutMutation(deletedId).unwrap();
      dispatch(apiRoomNate.util.invalidateTags(["savedLayouts"]));
      if (selectedFolderId === deletedId) {
        setSelectedFolderId(null);
      }
    } catch (err) {
      console.error("Delete folder failed:", err);
    } finally {
      setFolderPendingDelete(null);
    }
  }, [
    folderPendingDelete,
    reparentDirectChildrenToRoot,
    deleteLayoutMutation,
    dispatch,
    selectedFolderId,
  ]);

  const handleUploadComplete = useCallback(
    async (uuid) => {
      const folderId = selectedFolderIdRef.current;
      if (folderId == null || !uuid || !currentUserSessionStorage) return;
      try {
        const { data: tree } = await refetchLayouts();
        const plan = findLayoutByUuid(tree || [], uuid);
        if (!plan?._id) return;
        await axios.put(
          `${baseUrl}/savedLayout/edit/${plan._id}`,
          { parent: folderId },
          { headers: authHeaders() }
        );
        dispatch(apiRoomNate.util.invalidateTags(["savedLayouts"]));
      } catch (e) {
        console.error("Assign upload to folder failed:", e);
      }
    },
    [refetchLayouts, authHeaders, dispatch, currentUserSessionStorage]
  );

  const handlePlanDragStart = useCallback((e, planId) => {
    if (!isMdUp) return;
    e.dataTransfer.setData("text/plain", String(planId));
    e.dataTransfer.effectAllowed = "move";
    setDraggingPlanId(planId);
  }, [isMdUp]);

  const handlePlanDragEnd = useCallback(() => {
    setDraggingPlanId(null);
    setDropTargetFolderId(null);
  }, []);

  const handleFolderDragOver = useCallback(
    (e, folderId) => {
      if (!isMdUp || !draggingPlanId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTargetFolderId(folderId);
    },
    [isMdUp, draggingPlanId]
  );

  const handleFolderDragLeave = useCallback(
    (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setDropTargetFolderId(null);
      }
    },
    []
  );

  const handleFolderDrop = useCallback(
    async (e, folderId) => {
      e.preventDefault();
      if (!isMdUp) return;
      const raw = e.dataTransfer.getData("text/plain");
      const planId = raw ? String(raw) : "";
      setDropTargetFolderId(null);
      setDraggingPlanId(null);
      if (!planId || !folderId) return;
      try {
        await axios.put(
          `${baseUrl}/savedLayout/edit/${planId}`,
          { parent: folderId },
          { headers: authHeaders() }
        );
        dispatch(apiRoomNate.util.invalidateTags(["savedLayouts"]));
      } catch (err) {
        console.error("Drop assign folder failed:", err);
      }
    },
    [isMdUp, authHeaders, dispatch]
  );

  const allPlansFlat = useMemo(() => {
    if (!layoutData?.length) return [];
    return layoutData
      .flatMap(filterLayoutsNode)
      .filter((i) => i.image && i?.userSavedLayoutsCount > 0);
  }, [layoutData]);

  const totalPlanCount = allPlansFlat.length;

  const foldersAlphabetical = useMemo(
    () => collectFoldersFlatSorted(layoutData),
    [layoutData]
  );

  /**
   * Folder-aware ordered plan list for the mobile slider — mirrors the
   * `bentoFloorPlanItems` selector inside `SketchUpload` so both views show
   * the same plans in the same order.
   */
  const mobileFloorPlanItems = useMemo(() => {
    if (selectedFolderId == null) {
      return allPlansFlat.slice().reverse();
    }
    const folder = findLayoutByFolderId(layoutData, selectedFolderId);
    if (!folder) return allPlansFlat.slice().reverse();
    return filterLayoutsNode(folder)
      .filter((i) => i.image && i?.userSavedLayoutsCount > 0)
      .slice()
      .reverse();
  }, [layoutData, selectedFolderId, allPlansFlat]);

  const mobileFloorPlanSectionTitle = useMemo(() => {
    const count = mobileFloorPlanItems?.length ?? 0;
    if (selectedFolderId == null) {
      return count > 0 ? `All plans (${count})` : "All plans";
    }
    const folder = findLayoutByFolderId(layoutData, selectedFolderId);
    const name = folder?.name || "Floor plans";
    return count > 0 ? `${name} (${count})` : name;
  }, [layoutData, selectedFolderId, mobileFloorPlanItems]);

  /** Avoid showing “No floor plans yet” while GET /savedLayout/ is still in flight or refetching with an empty list. */
  const mobileLibraryLoading =
    mobileFloorPlanItems.length === 0 &&
    (savedLayoutsLoading || savedLayoutsFetching);

  const handleMobileCardClick = useCallback(
    (item) => {
      sketchUploadHandleRef.current?.openFloorPlanCard?.(item);
    },
    []
  );

  const handleMobileCardDoubleClick = useCallback(
    (item) => {
      sketchUploadHandleRef.current?.openFloorPlanCardDoubleClick?.(item);
    },
    []
  );

  const handleMobileMoreOptionsClick = useCallback((item) => {
    sketchUploadHandleRef.current?.openOptionsMenu?.(item);
  }, []);

  const folderListInsetPx = 2;

  const folderRowSx = useCallback(
    (selected, isDropHighlight) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: folderListInsetPx,
      py: "8px",
      borderRadius: "9999px",
      cursor: "pointer",
      boxSizing: "border-box",
      width: "100%",
      minWidth: 0,
      bgcolor: isDropHighlight
        ? alpha(theme.palette.primary.main, 0.12)
        : selected
        ? alpha(theme.palette.customPrimary.main, 0.045)
        : "transparent",
      borderLeft: isDropHighlight ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
      "&:hover": {
        bgcolor: isDropHighlight
          ? alpha(theme.palette.primary.main, 0.14)
          : selected
          ? alpha(theme.palette.customPrimary.main, 0.09)
          : "action.hover",
      },
    }),
    [theme]
  );

  return (
    <Box
      sx={{
        width: "100%",
        flex: libraryFillHeight ? 1 : "none",
        minHeight: libraryFillHeight ? 0 : "auto",
        boxSizing: "border-box",
        bgcolor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: libraryFillHeight ? "hidden" : "visible",
        pt: BENTO_SPACING,
        pb: BENTO_SPACING,
        px: { xs: 1.25, sm: 2 },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          maxWidth: 1200,
          mx: "auto",
          width: "100%",
          mb: BENTO_SPACING,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          aria-label="MagicFurnish"
          sx={{
            height: { xs: 36, sm: 42 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            "& svg": { height: "100%", width: "auto", display: "block" },
          }}
        >
          <MfLogo aria-hidden focusable="false" />
        </Box>
        <MainHomeHeaderActions />
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1, sm: 1.5, md: BENTO_SPACING }}
        sx={{
          flex: libraryFillHeight ? 1 : "none",
          minHeight: libraryFillHeight ? 0 : "auto",
          maxWidth: 1200,
          // On mobile/tablet, cancel the parent (`UserMainHome` outer Box)
          // horizontal padding so the Stack — and therefore the
          // MobileFloorPlanSlider inside it — reaches the viewport edges. On
          // desktop, fall back to `auto` to keep the 1200px layout centered.
          mx: { xs: -1.25, sm: -2, md: "auto" },
          // On mobile, leave width auto so `align-self: stretch` can grow the
          // item to `parent_content_width - margin_left - margin_right`. With
          // negative horizontal margins this resolves to the full viewport
          // width. Explicit `width: 100%` would pin the item to the parent's
          // content box (viewport - 20px) and the right edge would stay
          // inset by exactly the page padding. On desktop, restore
          // `width: 100%` so the centered 1200px layout fills as before.
          width: { xs: "auto", md: "100%" },
          alignItems: { xs: "stretch", md: "stretch" },
          boxSizing: "border-box",
        }}
      >
        <Stack
          spacing={BENTO_SPACING}
          sx={{
            width: "100%",
            maxWidth: CARD_MAX_WIDTH,
            alignSelf: { xs: "center", md: "stretch" },
            flex: { md: "1 1 auto" },
            minWidth: 0,
            minHeight: { md: 0 },
            alignItems: "stretch",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
                pl: folderListInsetPx,
                pr: folderListInsetPx,
                pt: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                }}
              >
                Folders
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowNewFolder(true)}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0.25,
                  mr: "-4px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "9999px",
                }}
              >
                + New
              </Button>
            </Box>

            <Box
              component="div"
              role="list"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                maxHeight: { md: 320 },
                overflowY: { md: "auto" },
              }}
            >
              <Box
                role="listitem"
                tabIndex={0}
                onClick={() => setSelectedFolderId(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedFolderId(null);
                  }
                }}
                sx={folderRowSx(selectedFolderId == null, false)}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                    position: "relative",
                    left: { xs: 0, md: "-2px" },
                  }}
                >
                  All plans
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                  {totalPlanCount}
                </Typography>
              </Box>

              {foldersAlphabetical.map((folder) => {
                const count = countPlansUnderFolderNode(folder);
                const sel = selectedFolderId === folder._id;
                const isDrop = dropTargetFolderId === folder._id && Boolean(draggingPlanId);
                const isEditing = editingFolderId === folder._id && isMdUp;
                return (
                  <Box
                    key={folder._id}
                    role="listitem"
                    tabIndex={0}
                    onClick={() => handleFolderRowClick(folder._id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleFolderRowClick(folder._id);
                      }
                    }}
                    onContextMenu={(e) => handleFolderContextMenu(e, folder)}
                    onTouchStart={(e) => touchStartFolder(e, folder)}
                    onTouchEnd={touchClearLongPress}
                    onTouchMove={touchClearLongPress}
                    onTouchCancel={touchClearLongPress}
                    onDragOver={(e) => handleFolderDragOver(e, folder._id)}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={(e) => handleFolderDrop(e, folder._id)}
                    sx={folderRowSx(sel, isDrop)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                        flex: 1,
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        beginDesktopRename(folder);
                      }}
                    >
                      <FolderOutlinedIcon
                        sx={{ fontSize: 16, mr: 1, flexShrink: 0, color: "text.secondary" }}
                      />
                      {isEditing ? (
                        <InputBase
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={handleRenameKeyDown}
                          onBlur={commitRename}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            "aria-label": "Folder name",
                            style: {
                              padding: 0,
                              fontSize: "13px",
                              fontWeight: 500,
                            },
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "13px",
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.primary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {folder.name || "Untitled"}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                        flexShrink: 0,
                        ml: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <UploadFlowPanel
              layoutMode="embedded"
              onUploadComplete={handleUploadComplete}
            />
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: "100%",
              pl: folderListInsetPx,
              pr: folderListInsetPx,
              mt: "auto",
              pt: 2,
              boxSizing: "border-box",
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: "10px", color: "rgba(0,0,0,0.3)" }}
            >
              <Box
                component="a"
                href={MF_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={legalLinkSx}
              >
                Terms
              </Box>
              {" · "}
              <Box
                component="a"
                href={MF_PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={legalLinkSx}
              >
                Privacy
              </Box>
              {" · "}
              <Box
                component="a"
                href={MF_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={legalLinkSx}
              >
                Support
              </Box>
              {" · "}
              <Box component="span" sx={legalVersionLabelSx}>
                {APP_VERSION}
              </Box>
            </Box>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            // Below md the floor-plan UI is owned by MobileFloorPlanSlider, so
            // hide the Paper entirely on mobile. SketchUpload still mounts and
            // its dialogs/menus (LayoutOptionMenu, NewFolderDialog, etc.) keep
            // working because they Portal to document.body.
            display: { xs: "none", md: "flex" },
            flex: libraryFillHeight ? 1 : { xs: "none", md: 1 },
            minHeight: libraryFillHeight ? 0 : "auto",
            minWidth: 0,
            flexDirection: "column",
            borderRadius: BENTO_SURFACE_BORDER_RADIUS,
            border: "1px solid rgba(0,0,0,0.08)",
            bgcolor: "#FFFFFF",
            boxShadow: "none",
            overflow: libraryFillHeight ? "hidden" : "visible",
            width: "100%",
          }}
        >
          <SketchUpload
            mainHomeEmbedded
            dashboardShell="bento"
            mainHomeBentoFill={libraryFillHeight}
            selectedFolderId={selectedFolderId}
            setSelectedFolderId={setSelectedFolderId}
            showNewFolder={showNewFolder}
            setShowNewFolder={setShowNewFolder}
            bentoDraggingPlanId={draggingPlanId}
            onBentoPlanDragStart={handlePlanDragStart}
            onBentoPlanDragEnd={handlePlanDragEnd}
            externalOptionsMenuHandle={sketchUploadHandleRef}
          />
        </Paper>

        <Box sx={{ mt: { xs: "14px", md: 0 }, minWidth: 0 }}>
          <MobileFloorPlanSlider
            floorPlanItems={mobileFloorPlanItems}
            sectionTitle={mobileFloorPlanSectionTitle}
            loading={mobileLibraryLoading}
            onCardClick={handleMobileCardClick}
            onCardDoubleClick={handleMobileCardDoubleClick}
            onMoreOptionsClick={handleMobileMoreOptionsClick}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "block", md: "none" },
            width: "100%",
            maxWidth: CARD_MAX_WIDTH,
            alignSelf: "center",
            mt: "34px",
            px: 0,
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: "10px", color: "rgba(0,0,0,0.3)" }}
          >
            <Box
              component="a"
              href={MF_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Terms
            </Box>
            {" · "}
            <Box
              component="a"
              href={MF_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Privacy
            </Box>
            {" · "}
            <Box
              component="a"
              href={MF_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalLinkSx}
            >
              Support
            </Box>
            {" · "}
            <Box component="span" sx={legalVersionLabelSx}>
              {APP_VERSION}
            </Box>
          </Box>
        </Box>
      </Stack>

      <Popover
        open={Boolean(mobilePopover)}
        anchorEl={mobilePopover?.anchorEl}
        onClose={closeMobileFolderPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 280, boxSizing: "border-box" }}>
          <InputBase
            fullWidth
            autoFocus
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (mobilePopover?.folder?._id) {
                  saveFolderName(mobilePopover.folder._id, renameDraft).then(() => {
                    closeMobileFolderPopover();
                  });
                }
              } else if (e.key === "Escape") {
                e.preventDefault();
                closeMobileFolderPopover();
              }
            }}
            inputProps={{
              style: { fontSize: "16px", paddingTop: 4, paddingBottom: 4 },
            }}
            sx={{ mb: 1.5 }}
          />
          <Button
            fullWidth
            sx={{ color: "error.main", textTransform: "none", justifyContent: "flex-start" }}
            onClick={() => {
              if (mobilePopover?.folder) {
                setFolderPendingDelete(mobilePopover.folder);
                closeMobileFolderPopover();
              }
            }}
          >
            Delete folder
          </Button>
        </Box>
      </Popover>

      <Menu
        open={Boolean(contextMenu)}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu ? { top: contextMenu.top, left: contextMenu.left } : undefined
        }
      >
        <MenuItem
          onClick={() => {
            if (contextMenu?.folder) beginDesktopRename(contextMenu.folder);
            closeContextMenu();
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            if (contextMenu?.folder) setFolderPendingDelete(contextMenu.folder);
            closeContextMenu();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <DeleteDialog
        isOpen={Boolean(folderPendingDelete)}
        instruction={
          folderPendingDelete
            ? `Delete ${folderPendingDelete.name || "Untitled"}?`
            : ""
        }
        helper="Plans inside will be moved to All plans."
        onAccept={confirmDeleteFolder}
        acceptedText="Delete"
        onCancel={() => setFolderPendingDelete(null)}
      />
    </Box>
  );
}

export default UserMainHome;
